/**
 * `<EvaluationProgressBanner>` — cycling phase indicator shown above the
 * skeleton while an AI evaluation OR an Interview Prep generation is in flight.
 *
 * Spec: candidate-detail-rebuild-spec.md §6.5.2 (AI eval) +
 *       interview-prep-spec.md §3.3 (prep generating).
 *
 * Phases cycle every 7s; total expected eval/prep time is up to a minute.
 * This is a UX-only progress signal — no real backend telemetry.
 *
 * Phase 2.6 amendment (2026-05-10): added optional phase-text override
 * (`phases` + `headline`) so the Interview Prep tab can reuse the same
 * component instead of forking a parallel `PrepGenerationStatus.tsx`.
 * The default phase set targets AI Evaluation copy; Prep callers pass
 * the spec §3.3 prep-specific phases.
 *
 * Props:
 *   `candidateName` — first name only, for the banner copy.
 *   `jdName` — the JD title being evaluated against.
 *   `phases` — optional override for phase strings. Defaults to AI-eval
 *              phase set.
 *   `headline` — optional override for the headline copy. Defaults to
 *               "Evaluating {firstName} against {jdName}…".
 */
import { useEffect, useState, type JSX } from "react";
import { Clock } from "lucide-react";
import { Spinner } from "@/components/common/Spinner";

const DEFAULT_PHASES = [
  "Reading resume…",
  "Comparing against requirements…",
  "Drafting strengths and gaps…",
  "Finalizing recommendation…",
] as const;

const PHASE_INTERVAL_MS = 7_000;

interface EvaluationProgressBannerProps {
  candidateName: string;
  jdName: string;
  /** Optional phase override (Phase 2.6 reuse for Interview Prep). */
  phases?: readonly string[];
  /** Optional headline override (Phase 2.6 reuse for Interview Prep). */
  headline?: string;
}

export function EvaluationProgressBanner({
  candidateName,
  jdName,
  phases,
  headline,
}: EvaluationProgressBannerProps): JSX.Element {
  const [phaseIndex, setPhaseIndex] = useState(0);

  const phaseList = phases && phases.length > 0 ? phases : DEFAULT_PHASES;

  useEffect(() => {
    const id = setInterval(() => {
      setPhaseIndex((i) => (i + 1) % phaseList.length);
    }, PHASE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phaseList.length]);

  const firstName = candidateName.split(" ")[0] ?? candidateName;
  const defaultHeadline = `Evaluating ${firstName} against ${jdName}…`;

  return (
    <div
      className="mb-4 flex items-center gap-3 rounded-lg bg-surface-subtle p-3"
      data-testid="eval-progress-banner"
      role="status"
      aria-live="polite"
    >
      <Spinner size="sm" className="text-accent-secondary" />
      <div className="flex flex-col">
        <span className="text-sm text-text-primary">
          {headline ?? defaultHeadline}
        </span>
        <span className="text-xs text-text-secondary">
          {phaseList[phaseIndex]}
        </span>
        <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-text-tertiary">
          <Clock size={12} aria-hidden />
          This usually takes up to a minute. You can leave this page — it keeps
          running and will be here when you come back.
        </span>
      </div>
    </div>
  );
}
